import { useForm } from "react-hook-form";
import { useEffect, useState, useCallback, useMemo } from "react";
import { DateTimePickerForm } from "@/components/Common/form/DateTimePicker.Field";
import { Input } from "@/components/Base/Input";
import { DEFAULT_TIME_OPTIONS, TimeUntilSelector } from "./TimeUntilSelector";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { TextareaField } from "@/components/Common/form/TextareaField";
import { Icon } from "@/components/Common/Icon";
import {
  Card,
  CustomerUpdate,
  CustomerUpdateMedium,
  CustomerUpdateMediumLabels,
  CustomerUpdateStatus,
  Employee,
  IconNames,
  Shop,
  TerminologyOptions,
} from "@/types/v2";
import { Button } from "@/components/Base/Button";
import { getCardFieldLabel, getCardSection } from "@/lib/cards";
import { Separator } from "@/components/Base/Separator";
import { getContainerName } from "@/lib/getContainerName";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { getServiceAdvisorColor } from "@/lib/employees";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN } from "@/lib/const";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { Tooltip } from "@/components/Common/Tooltip";
import { formatCustomerName } from "@/lib/customer";
import { firestoreToDateTime, formatDate } from "@/lib/utils";
import CustomerUpdateTimelineEntry from "@/components/CustomerUpdate/CustomerUpdateTimelineEntry";
import { useAvailableUpdateTime } from "@/hooks/queries/useAvailableUpdateTime";
import { useDebounceCallback } from "@/hooks/useDebounceCallback";
import { differenceInMinutes } from "date-fns";
import {
  CustomerUpdateUsageLocation,
  useCustomerUpdatesAnalytics,
} from "@/hooks/useCustomerUpdatesAnalytics";
import { CheckboxInput } from "@/components/Base/CheckboxInput";
import { useCustomerUpdatesConfig } from "@/hooks/shop/useCustomerUpdatesConfig";
import { ExternalLinkWrapper } from "../Dashboard/components/Card/ExternalLinkWrapper";
import { useCardFiltering } from "@/hooks/useCardFiltering";
import { useCustomerUpdatesByCardId } from "@/hooks/queries/useCustomerUpdatesByCardId";
import CustomerUpdatesTimeline from "@/components/CustomerUpdate/CustomerUpdatesTimeline";

// Create a base schema for type inference
const baseCustomerUpdateFormSchema = z.object({
  desiredUpdateTime: z.date({
    required_error: "Reminder time is required",
    invalid_type_error: "Reminder time must be a valid date",
  }),
  comments: z.string().optional(),
  skipped: z.boolean().optional(),
  skipReason: z.string().optional(),
  timeUntil: z
    .number()
    .int()
    .positive({ message: "Time until must be a positive number" })
    .optional()
    .refine((val) => val !== undefined, {
      message: "Time until is required",
    }),
  userId: z.string().optional(),
  type: z.enum(["new", "update", "skip"]).optional(),
  isFinal: z.boolean().optional(),
  updateMedium: z.array(z.string()).optional(),
});

// Export the type based on the base schema
export type CustomerUpdateFormValues = z.infer<
  typeof baseCustomerUpdateFormSchema
>;

// Function to create schema with dynamic validation
export const createCustomerUpdateFormSchema = (requireComments: boolean) =>
  baseCustomerUpdateFormSchema.superRefine((data, ctx) => {
    // if "skipped" is true, "skipReason" must be present
    if (data.skipped && !data.skipReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Reason for skipping is required when update is skipped",
        path: ["skipReason"],
      });
    }

    // A Remind user is required whenever a next reminder will be scheduled.
    // The Remind field is only shown when isFinal is false, so require it
    // there too — otherwise the save silently omits the assignee and the
    // next customer update can never be created.
    if (!data.isFinal && !data.userId?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A user must be selected to remind for the next update",
        path: ["userId"],
      });
    }

    // If requireComments is true, validate based on the type
    if (requireComments) {
      // For skip type, skipReason is required
      if (
        data.type === "skip" &&
        (!data.skipReason || data.skipReason.trim() === "")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Reason for skipping is required",
          path: ["skipReason"],
        });
      }

      // For update type, comments are required
      if (
        data.type === "update" &&
        (!data.comments || data.comments.trim() === "")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Comments are required",
          path: ["comments"],
        });
      }
    }
  });

// A CustomerUpdate's stored userId can point to a service advisor who is no
// longer selectable (hidden, or missing a shopHeroAuthUid) — in that case
// the Remind dropdown renders as blank, but the stale userId would still
// satisfy the "Remind is required" check above since it's non-empty. Only
// keep the stored value as the form default when it matches someone
// actually shown in the dropdown; otherwise fall back to "" so the user is
// forced to pick a currently valid person.
export const resolveEligibleRemindUserId = (
  userId: string | null | undefined,
  eligibleServiceAdvisors: Pick<Employee, "shopHeroAuthUid">[]
): string => {
  if (!userId) return "";
  return eligibleServiceAdvisors.some((sa) => sa.shopHeroAuthUid === userId)
    ? userId
    : "";
};

export type CustomerUpdateFormProps = {
  initialValues?: Partial<CustomerUpdate> | null;
  onSubmit: (values: CustomerUpdateFormValues) => Promise<void> | void;
  onSnooze?: () => Promise<void> | void;
  onSnoozeAll?: () => Promise<void> | void;
  snoozeAllEnabled?: boolean;
  cardData?: Card | null;
  shop?: Shop; // TODO: Revamp to decrease need for full shop object
  serviceAdvisors: Employee[];
  submitting?: boolean;
  snoozeTime?: number; // Default snooze time in minutes
  location: CustomerUpdateUsageLocation;
};

export const CustomerUpdateForm = ({
  initialValues,
  onSubmit,
  onSnooze,
  onSnoozeAll,
  snoozeAllEnabled,
  cardData,
  shop,
  serviceAdvisors,
  submitting,
  snoozeTime = DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN,
  location = CustomerUpdateUsageLocation.PAGE, // Default to page if not provided
}: CustomerUpdateFormProps) => {
  const { terminolize } = useTerminologyReplacer();
  const { logCustomTimeUsed, logUserChanged, logQuickTimeUsed } =
    useCustomerUpdatesAnalytics();

  const customerUpdatesConfig = useCustomerUpdatesConfig();
  const requireComments = customerUpdatesConfig.requireComments ?? false;

  const { updateCardSearch } = useCardFiltering();

  const [showAllHistory, setShowAllHistory] = useState(false);
  // Get all customer updates for this card
  const {
    data: customerUpdates = [],
    isLoading: isLoadingCustomerUpdates,
    error: customerUpdatesError,
  } = useCustomerUpdatesByCardId({
    shopId: shop?.id ?? "",
    cardId: cardData?.id ?? "",
    enabled: showAllHistory, // Only fetch when showAllHistory is enabled
  });

  const [localSubmitting, setLocalSubmitting] = useState(false);
  // Default Desired Update time
  const defaultDesiredUpdateTime = new Date().setMinutes(
    new Date().getMinutes() +
      (shop?.shopConfig?.customerUpdates?.quickTimeOptions?.[0]?.value ??
        DEFAULT_TIME_OPTIONS[0].value)
  );
  const disabledStatuses = new Set([
    CustomerUpdateStatus.COMPLETED,
    CustomerUpdateStatus.SKIPPED,
  ]);

  const disabled =
    submitting ||
    localSubmitting ||
    (initialValues?.status &&
      disabledStatuses.has(initialValues.status as any)); // eslint-disable-line @typescript-eslint/no-explicit-any

  const eligibleServiceAdvisors = useMemo(
    () =>
      serviceAdvisors?.filter(
        (sa) => !sa.hideInShopHero && !!sa.shopHeroAuthUid
      ) ?? [],
    [serviceAdvisors]
  );

  const defaultInitialValues: CustomerUpdateFormValues = {
    desiredUpdateTime: new Date(defaultDesiredUpdateTime),
    comments: "",
    skipped: false,
    skipReason: "",
    userId: "",
    timeUntil: 30, // Default to 30 minutes
    type: "new",
    updateMedium: [CustomerUpdateMedium.PHONE],
    isFinal: false, // Default to false
  };

  const customerUpdateFormSchema =
    createCustomerUpdateFormSchema(requireComments);

  const form = useForm<CustomerUpdateFormValues>({
    resolver: zodResolver(customerUpdateFormSchema),
    defaultValues: {
      ...defaultInitialValues,
      userId: resolveEligibleRemindUserId(
        initialValues?.userId,
        eligibleServiceAdvisors
      ),
      type:
        initialValues?.status !== CustomerUpdateStatus.CREATED
          ? "update"
          : "new",
    },
  });

  const { watch } = form;
  const watchedUserId = watch("userId");
  const watchedDesiredUpdateTime = watch("desiredUpdateTime");

  // Create stable values to prevent infinite loops
  const [stableDesiredTime, setStableDesiredTime] = useState<Date | null>(null);
  const [stableUserId, setStableUserId] = useState<string>("");

  // Debounced callback to update stable values - memoized to prevent re-creation
  const debouncedUpdateTime = useDebounceCallback(
    useCallback((time: Date, userId: string) => {
      setStableDesiredTime(time);
      setStableUserId(userId);
    }, []),
    500
  );

  // Update stable values when watched values change
  useEffect(() => {
    if (watchedDesiredUpdateTime && watchedUserId) {
      debouncedUpdateTime(watchedDesiredUpdateTime, watchedUserId);
    }
  }, [watchedDesiredUpdateTime, watchedUserId, debouncedUpdateTime]);

  // Use the hook to get available update time with stable values
  const { data: availableUpdateTime } = useAvailableUpdateTime({
    shopId: shop?.id || "",
    userId: stableUserId,
    desiredUpdateTime: stableDesiredTime || new Date(),
    enabled: !!(shop?.id && stableUserId && stableDesiredTime),
  });

  // Update form values when initialValues change
  useEffect(() => {
    if (initialValues) {
      const newValues = {
        ...defaultInitialValues,
        timeUntil:
          shop?.shopConfig?.customerUpdates?.quickTimeOptions?.[0]?.value ??
          DEFAULT_TIME_OPTIONS[0].value,
        userId: resolveEligibleRemindUserId(
          initialValues.userId,
          eligibleServiceAdvisors
        ),
        type:
          initialValues.status !== CustomerUpdateStatus.CREATED
            ? "update"
            : "new",
        comments: initialValues.comments ?? "",
        skipReason: initialValues.skippedReason ?? "",
      };

      // `initialValues` is a new object on every parent re-render whenever
      // ANY customer update in the shop changes (subscribeAll's onSnapshot
      // maps every doc to a fresh object each emission), not just this one.
      // `keepDirtyValues` stops that churn from clobbering fields the user
      // has already started editing (e.g. reassigning the SA) — untouched
      // fields still pick up the latest values normally.
      form.reset(newValues as CustomerUpdateFormValues, {
        keepDirtyValues: true,
      });

      // Also update local state if needed
      setTimeUntil(newValues.timeUntil);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, shop?.shopConfig?.customerUpdates?.quickTimeOptions]);

  // `serviceAdvisors` loads asynchronously (Firestore listener), so it can
  // still be empty when the effect above first resolves `initialValues`,
  // which makes `eligibleServiceAdvisors` empty and incorrectly clears a
  // perfectly valid stored userId. Re-resolve once service advisors catch
  // up, but leave the field alone if the user has already picked something.
  useEffect(() => {
    if (!initialValues || form.formState.dirtyFields.userId) return;
    const resolvedUserId = resolveEligibleRemindUserId(
      initialValues.userId,
      eligibleServiceAdvisors
    );
    if (resolvedUserId !== form.getValues("userId")) {
      form.setValue("userId", resolvedUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleServiceAdvisors, initialValues]);

  const [timeUntil, setTimeUntil] = useState<number | undefined>(
    shop?.shopConfig?.customerUpdates?.quickTimeOptions?.[0]?.value ??
      DEFAULT_TIME_OPTIONS[0].value
  );

  const handleTimeUntilChange = (value: number) => {
    setTimeUntil(value);
    form.setValue("timeUntil", value);
  };

  const handleSubmit = form.handleSubmit(
    (data) => {
      setLocalSubmitting(true);
      Promise.resolve(onSubmit(data)).finally(() => setLocalSubmitting(false));
    },
    (errors) => {
      console.error("Validation errors:", errors);
      // This will run when validation fails
    }
  );

  const handleSnooze = async () => {
    if (onSnooze) {
      setLocalSubmitting(true);
      Promise.resolve(onSnooze()).finally(() => setLocalSubmitting(false));
    }
  };

  const handleSnoozeAll = async () => {
    if (onSnoozeAll) {
      setLocalSubmitting(true);
      Promise.resolve(onSnoozeAll()).finally(() => setLocalSubmitting(false));
    }
  };

  const handleUpdateCardSearch = () => {
    if (cardData && cardData.cardNumber) {
      updateCardSearch(cardData.cardNumber);
    }
  };

  const instructions = {
    new: "Select below when you would like the first customer update to occur.",
    update: "Provide a customer update and schedule the next reminder below.",
    skip: "Provide a customer update and schedule the next reminder below.",
  };

  return (
    <Form {...form}>
      <form
        id="customer-update-form"
        onSubmit={handleSubmit}
        className="w-full space-y-4"
      >
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <Icon
            name={IconNames.bell}
            className={"h-6 w-6 fill-accent text-accent"}
            size={20}
          />
          <h2 className="text-base font-medium">Customer Update Reminder</h2>
          {initialValues?.status === CustomerUpdateStatus.CANCELLED && (
            <div className={"text-sm text-gray-700"}>
              Updates for this{" "}
              {terminolize("RO", TerminologyOptions.repairOrder, "acronym")}{" "}
              have been cancelled. To restart updates, provide an update and
              schedule another.
            </div>
          )}
          <div className={"text-sm text-gray-700"}>
            {instructions[watch("type") ?? "new"]}
          </div>
          {initialValues?.scheduledUpdateTime && (
            <div className={"text-sm text-gray-700"}>
              Due:{" "}
              {formatDate(
                firestoreToDateTime(initialValues.scheduledUpdateTime) as Date
              )}
            </div>
          )}
        </div>
        {cardData && shop && (
          <div className="flex w-full flex-row items-stretch justify-between gap-4 text-center text-sm">
            <div className="flex w-full flex-grow flex-col items-center rounded-md border border-input px-1 py-3">
              <div className="font-medium text-gray-500">
                {(location === CustomerUpdateUsageLocation.PANEL ||
                  location === CustomerUpdateUsageLocation.TOAST) && (
                  <button
                    type="button"
                    onClick={handleUpdateCardSearch}
                    className="mr-1 text-sm text-gray-700"
                  >
                    <Icon name={IconNames.search} size={12} />
                  </button>
                )}
                <span>
                  {terminolize("RO", TerminologyOptions.repairOrder, "acronym")}
                  #{" "}
                </span>
                <ExternalLinkWrapper
                  cardId={
                    cardData.source === "manual" ? undefined : cardData.id
                  }
                >
                  <span className="font-bold text-gray-700">
                    {cardData.cardNumber}
                  </span>
                </ExternalLinkWrapper>
              </div>
              <div className="font-medium text-gray-500">
                <Tooltip
                  message={getCardFieldLabel(shop, "secondaryField")}
                  className={"z-[100]"}
                >
                  <span className="font-bold text-gray-700">
                    {getCardSection(cardData, shop, "secondaryField")}
                  </span>
                </Tooltip>
              </div>
              <div className="font-medium text-gray-500">
                <Tooltip
                  message={getCardFieldLabel(shop, "tertiaryField")}
                  className={"z-[100]"}
                >
                  <span className={"font-bold text-gray-700"}>
                    {getCardSection(cardData, shop, "tertiaryField")}
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className="flex w-full flex-col items-center rounded-md border border-input px-1 py-3">
              <div className="font-bold text-gray-700">Customer Info</div>
              <div className="font-medium text-gray-500">
                {cardData.customer
                  ? formatCustomerName(cardData.customer)
                  : "No customer"}
              </div>
              <div className={"font-medium text-gray-500"}>
                {cardData?.customer?.phone && cardData.customer.phone.length > 0
                  ? cardData.customer.phone[0].number
                  : "No phone number"}
              </div>
            </div>

            <div className="flex w-full flex-col items-center rounded-md border border-input px-1 py-3">
              <div className="text-sm font-medium">Container</div>
              <div className="text-sm capitalize text-gray-700">
                {getContainerName(cardData.currentContainer, shop.containers)}
              </div>
              <div className="text-xs">
                Since:{" "}
                {firestoreToDateTime(cardData.enteredContainerAt)
                  ? formatDate(
                      firestoreToDateTime(cardData.enteredContainerAt) as Date
                    )
                  : "N/A"}
              </div>
            </div>
          </div>
        )}
        <Separator />

        {initialValues?.lastCustomerUpdate && (
          <>
            <div className={"mb-2"}>
              <div className="mb-1 flex flex-row items-center justify-between gap-4">
                <div className="text-sm font-medium">
                  Last Update{showAllHistory ? "s" : ""}
                </div>
                <Button
                  type="button"
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  size={"sm"}
                  variant="outline"
                  className="h-fit px-2 py-0.5 text-xs"
                  disabled={isLoadingCustomerUpdates}
                >
                  {showAllHistory ? "Hide" : "View"} All History
                </Button>
              </div>
              {showAllHistory && cardData && (
                <>
                  {isLoadingCustomerUpdates ? (
                    <div className="justify-centerpy-8 flex items-center">
                      <div className="flex items-center gap-2">
                        <Icon
                          name={IconNames.loader}
                          className="h-5 w-5 animate-spin"
                        />
                        <span>Loading customer updates...</span>
                      </div>
                    </div>
                  ) : customerUpdatesError ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Icon
                        name={IconNames.warning}
                        className="mb-2 h-8 w-8 text-destructive"
                      />
                      <h3 className="mb-1 text-lg font-medium text-destructive">
                        Error Loading Customer Updates
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Unable to load customer updates. Please try again.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-auto">
                      <CustomerUpdatesTimeline
                        updates={customerUpdates.filter(
                          (cu) => cu.id !== initialValues.id
                        )}
                        card={cardData}
                        compact={false}
                        initiallyOpenCount={0}
                        showHeader={false}
                        emptyState={
                          <div className="flex flex-col items-center justify-center py-8 text-center">
                            <Icon
                              name="bell"
                              className="mb-2 h-8 w-8 text-muted-foreground"
                            />
                            <h3 className="mb-1 text-lg font-medium text-muted-foreground">
                              No Customer Updates
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              There are no customer updates for this card yet.
                            </p>
                          </div>
                        }
                      />
                    </div>
                  )}
                </>
              )}
              {!showAllHistory && (
                <ul className="space-y-2">
                  <CustomerUpdateTimelineEntry
                    update={initialValues.lastCustomerUpdate}
                    employees={serviceAdvisors}
                    showCardInfo
                    dense
                    showMinimalTimestamps
                    collapsedNotes
                  />
                </ul>
              )}
            </div>
            <Separator />
          </>
        )}
        <>
          {watch("type") !== "new" && (
            <div className="flex flex-row items-center justify-between gap-4">
              <Button
                disabled={disabled}
                variant={watch("type") === "update" ? "accent" : "outline"}
                className="w-full"
                type={"button"}
                onClick={() => {
                  form.setValue("type", "update", { shouldDirty: true });
                }}
              >
                Give Customer Update
              </Button>
              <Button
                disabled={disabled}
                variant={watch("type") === "skip" ? "accent" : "outline"}
                className="w-full"
                type={"button"}
                onClick={() => {
                  form.setValue("type", "skip", { shouldDirty: true });
                  // Clear mediums when skipping
                  form.setValue("updateMedium", [], { shouldDirty: true });
                }}
              >
                Skip Customer Update
              </Button>
            </div>
          )}
          <div className={"mb-2"}>
            <TextareaField
              disabled={disabled}
              form={form}
              name={watch("type") === "skip" ? "skipReason" : "comments"}
              placeholder={
                watch("type") === "skip"
                  ? "Enter reason for skipping this update..."
                  : "Enter any comments or notes for this update..."
              }
              rows={4}
            />
          </div>
          {watch("type") !== "skip" ? (
            <div className="flex flex-row items-center justify-between gap-4">
              {[
                CustomerUpdateMedium.PHONE,
                CustomerUpdateMedium.TEXT,
                CustomerUpdateMedium.EMAIL,
                CustomerUpdateMedium.INPERSON,
              ].map((key) => (
                <CheckboxInput
                  key={key}
                  checked={
                    watch("updateMedium")?.includes(key) ?? "indeterminate"
                  }
                  className={"h-6 w-6 border border-primary"}
                  label={CustomerUpdateMediumLabels[key]}
                  disabled={disabled}
                  onClick={() => {
                    const currentMedium = form.getValues("updateMedium") || [];
                    if (currentMedium.includes(key)) {
                      // Remove the medium if already selected
                      form.setValue(
                        "updateMedium",
                        currentMedium.filter((m) => m !== key),
                        { shouldDirty: true }
                      );
                    } else {
                      // Add the medium if not selected
                      form.setValue("updateMedium", [...currentMedium, key], {
                        shouldDirty: true,
                      });
                    }
                  }}
                ></CheckboxInput>
              ))}
            </div>
          ) : (
            <div className="h-6" />
          )}
          <Separator />
        </>

        <h2 className="text-sm font-medium">Select One:</h2>

        <div className="flex flex-row items-center justify-between gap-4">
          <Button
            disabled={disabled}
            variant={!watch("isFinal") ? "accent" : "outline"}
            className="w-full"
            type={"button"}
            onClick={() => {
              form.setValue("isFinal", false, { shouldDirty: true });
            }}
          >
            {!watch("isFinal") && (
              <Icon name={IconNames.check} className="mr-2" />
            )}
            Set next reminder
          </Button>
          <Button
            disabled={disabled}
            variant={watch("isFinal") ? "accent" : "outline"}
            className="w-full"
            type={"button"}
            onClick={() => {
              form.setValue("isFinal", true, { shouldDirty: true });
            }}
          >
            {watch("isFinal") && (
              <Icon name={IconNames.check} className="mr-2" />
            )}
            No further updates needed
          </Button>
        </div>
        {!watch("isFinal") ? (
          <div className="flex min-h-40 flex-col gap-4">
            <div className="flex flex-row items-center justify-between gap-2">
              <FormField
                disabled={disabled}
                control={form.control}
                name="userId"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Remind</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value); // Update form field value
                          logUserChanged(location);
                        }}
                        value={field.value} // Ensure the correct value is displayed
                        disabled={form.formState.isSubmitting || disabled}
                      >
                        <SelectTrigger className="flex w-full flex-row items-center justify-start">
                          <Icon name={IconNames.user} className="mr-2" />
                          <SelectValue placeholder={"User"} />
                          <div className="flex-grow"></div>
                        </SelectTrigger>
                        <SelectContent className={"z-[100]"}>
                          {eligibleServiceAdvisors.map((serviceAdvisor) => (
                            <SelectItem
                              key={serviceAdvisor.shopHeroAuthUid}
                              value={`${serviceAdvisor.shopHeroAuthUid}`}
                            >
                              {/* Circle color next to the name */}
                              <span
                                className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                                style={{
                                  backgroundColor: getServiceAdvisorColor(
                                    serviceAdvisor.id,
                                    serviceAdvisors || []
                                  ),
                                }}
                              ></span>
                              {serviceAdvisor.firstName}{" "}
                              {serviceAdvisor.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Common date/time picker */}
            <div className="flex flex-row items-center justify-between gap-2">
              {location === CustomerUpdateUsageLocation.DIALOG ? (
                <FormField
                  control={form.control}
                  name="desiredUpdateTime"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Reminder Time</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          disabled={disabled}
                          value={
                            field.value
                              ? new Date(
                                  new Date(field.value).getTime() -
                                    new Date(field.value).getTimezoneOffset() *
                                      60000
                                )
                                  .toISOString()
                                  .slice(0, 16)
                              : ""
                          }
                          onChange={(e) => {
                            const value = e.target.value
                              ? new Date(e.target.value)
                              : null;
                            field.onChange(value);

                            if (value && value instanceof Date) {
                              const now = new Date();
                              const timeUntil = value.getTime() - now.getTime();
                              const minutesUntil = Math.ceil(
                                timeUntil / (1000 * 60)
                              );
                              handleTimeUntilChange(minutesUntil);

                              const selectorOptions = DEFAULT_TIME_OPTIONS.map(
                                (opt) => opt.value
                              );
                              if (
                                minutesUntil &&
                                !selectorOptions.includes(minutesUntil)
                              ) {
                                logCustomTimeUsed(location);
                              }
                            }
                          }}
                          className="text-primary [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:transition-opacity hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <DateTimePickerForm
                  disabled={disabled}
                  form={form}
                  name="desiredUpdateTime"
                  label="Reminder Time"
                  onClose={() => {
                    const selectorOptions = DEFAULT_TIME_OPTIONS.map(
                      (opt) => opt.value
                    );
                    // Only log if the time selected is not one of the quick select options
                    if (timeUntil && !selectorOptions.includes(timeUntil)) {
                      logCustomTimeUsed(location);
                    }
                  }}
                  onChange={(value) => {
                    if (value && value instanceof Date) {
                      const now = new Date();
                      const timeUntil = value.getTime() - now.getTime(); // Calculate time until in milliseconds
                      const minutesUntil = Math.ceil(timeUntil / (1000 * 60)); // Convert to minutes
                      handleTimeUntilChange(minutesUntil);
                    }
                  }}
                  zIndex={150}
                />
              )}

              {/* Time until selector */}
              <div className="text-gray-600">or</div>
              <FormField
                disabled={disabled}
                control={form.control}
                name="timeUntil"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Remind me in...</FormLabel>
                    <FormControl>
                      <TimeUntilSelector
                        disabled={disabled}
                        value={timeUntil}
                        options={
                          shop?.shopConfig?.customerUpdates?.quickTimeOptions
                        }
                        onChange={(value) => {
                          handleTimeUntilChange(value);
                          const now = new Date();
                          const desiredTime = now.setMinutes(
                            now.getMinutes() + value
                          );
                          // shouldDirty: this is the field actually
                          // persisted for scheduling (useCustomerUpdate.ts
                          // reads values.desiredUpdateTime, not timeUntil) —
                          // without it, picking a quick time is invisible to
                          // the keepDirtyValues guard above and reverts to
                          // the shop default on the next unrelated snapshot
                          // (WEBV3-47).
                          form.setValue(
                            "desiredUpdateTime",
                            new Date(desiredTime),
                            { shouldDirty: true }
                          );
                          field.onChange(value);
                          logQuickTimeUsed(location, timeUntil);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Display scheduled update time if available */}
            {stableDesiredTime &&
              availableUpdateTime &&
              !disabled &&
              differenceInMinutes(availableUpdateTime, stableDesiredTime) >
                0 && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={IconNames.clock}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="font-medium text-blue-900">
                      Expected Update Time:
                    </span>
                    <span className="text-blue-800">
                      {formatDate(availableUpdateTime)}
                    </span>
                  </div>
                  {stableDesiredTime &&
                    availableUpdateTime.getTime() !==
                      stableDesiredTime.getTime() && (
                      <div className="mt-1 text-xs text-blue-600">
                        Adjusted from your requested time due to other scheduled
                        customer updates.
                      </div>
                    )}
                </div>
              )}
          </div>
        ) : (
          <div className="flex h-40 flex-col items-center justify-center gap-4">
            <Icon name={IconNames.check} className="h-12 w-12 text-success" />
            <div className="mx-auto text-center text-gray-600">
              This will be the final customer update for this{" "}
              {terminolize("repair order", TerminologyOptions.repairOrder)}.
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant={"outline"} disabled={disabled}>
                  <Icon name={"moreHorizontal"} size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className={"z-[100]"} side={"left"}>
                {watch("type") !== "new" && snoozeAllEnabled && onSnoozeAll && (
                  <DropdownMenuItem
                    onClick={handleSnoozeAll}
                    disabled={disabled}
                  >
                    <Icon name={IconNames.bellOff} className="mr-2" />
                    <span>Snooze All Customer Updates</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    form.setValue("isFinal", true);
                    handleSubmit();
                  }}
                  className="text-destructive hover:cursor-pointer focus:bg-destructive focus:text-destructive-foreground"
                >
                  <Icon name={IconNames.warning} className="mr-2" />
                  <span>Complete and Cancel Future Updates</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {watch("type") !== "new" && (
              <Button
                type="button"
                variant={"outline"}
                onClick={handleSnooze}
                disabled={disabled}
              >
                Snooze ({snoozeTime} min)
              </Button>
            )}

            <Button
              variant="default"
              className="w-full"
              disabled={disabled}
              type="submit"
            >
              {submitting || localSubmitting
                ? "Saving..."
                : watch("isFinal")
                  ? "Save Last Update"
                  : "Save & Schedule"}
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4"></div>
        </div>
      </form>
    </Form>
  );
};
